'use strict';

import ActionTypes from '../constants/ActionTypes';
import Analytics from '../utils/Analytics';
import AnalyticsEvents from '../constants/AnalyticsEvents';
import AnalyticsSources from '../constants/AnalyticsSources';
import AppDispatcher from '../AppDispatcher';
import StampAPI from '../api/StampAPI';
import StampStore from '../stores/StampStore';
import UserStore from '../stores/UserStore';
import FeedStore from '../stores/FeedStore';
import TrendingStore from '../stores/TrendingStore';
import FeedWithTrendingStore from '../stores/FeedWithTrendingStore';
import UserLatestStampStore from '../stores/UserLatestStampStore';
import UserLikedStampStore from '../stores/UserLikedStampStore';
import ZineLatestStampStore from '../stores/ZineLatestStampStore';
import ZinePendingStampStore from '../stores/ZinePendingStampStore';
import SearchStampStore from '../stores/SearchStampStore';
import StampLikesStore from '../stores/StampLikesStore';
import ActionUtils from '../utils/ActionUtils';
import handleUnauthAction from '../utils/handleUnauthAction';
import parseStampSegment from '../utils/parseStampSegment';

function parseStampId(stampSegment) {
  return parseStampSegment(stampSegment).stampId;
}

var StampActionCreators = {
  requestStamp(stampSegment, fields) {
    ActionUtils.requestEntity({
      idAttribute: 'stampSegment',
      getIdForStore: parseStampId,
      stampSegment: stampSegment,
      store: StampStore,
      action: ActionTypes.REQUEST_STAMP,
      fields: fields,
      callAPI: StampAPI.requestStamp
    });
  },

  updateStamp(stamp) {
    ActionUtils.updateEntity({
      entity: stamp,
      action: ActionTypes.UPDATE_STAMP,
      callAPI: StampAPI.updateStamp
    });
  },

  deleteStamp(stampSegment) {
    ActionUtils.deleteEntity({
      idAttribute: 'stampSegment',
      stampSegment: stampSegment,
      action: ActionTypes.DELETE_STAMP,
      callAPI: StampAPI.deleteStamp
    });
  },

  likeStamp(stampSegment) {
    if (handleUnauthAction(AnalyticsSources.LIKE_STAMP)) {
      return;
    }

    AppDispatcher.handleViewAction({
      type: ActionTypes.LIKE_STAMP,
      stampSegment: stampSegment
    });

    StampAPI.likeStamp(stampSegment);
  },

  unlikeStamp(stampSegment) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.UNLIKE_STAMP,
      stampSegment: stampSegment
    });

    StampAPI.unlikeStamp(stampSegment);
  },

  incrementStampViews(stampSegment, isNextStamp) {
    AppDispatcher.handleViewAction({
      type: ActionTypes.INCREMENT_STAMP_VIEWS,
      stampSegment: stampSegment
    });

    StampAPI.incrementStampViews(stampSegment);
    Analytics.trackEvent(AnalyticsEvents.STAMP_VIEW, {
      stampSegment: stampSegment,
      isNextStamp: isNextStamp
    });
  },

  requestFeedWithTrendingPage(isInitialRequest, allowInvalidate) {
    if (allowInvalidate && isInitialRequest) {
      // Always keep feed and trending fresh
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_FEED_WITH_TRENDING
      });
    }

    if (FeedWithTrendingStore.needFeed(isInitialRequest)) {
      ActionUtils.requestPage({
        isInitialRequest: isInitialRequest,
        store: FeedStore,
        action: ActionTypes.REQUEST_FEED,
        callAPI: StampAPI.requestFeedPage
      });
    }

    if (FeedWithTrendingStore.needTrending(isInitialRequest)) {
      ActionUtils.requestPage({
        isInitialRequest: isInitialRequest,
        store: TrendingStore,
        action: ActionTypes.REQUEST_TRENDING_STAMPS,
        callAPI: StampAPI.requestTrendingPage
      });
    }
  },

  requestUserLatestPage(userId, isInitialRequest, allowInvalidate) {
    if (allowInvalidate && isInitialRequest && !UserStore.isMe(userId)) {
      // Keep other people's published stamps fresh
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_USER_LATEST_STAMPS,
        userId: userId
      });
    }

    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserLatestStampStore,
      action: ActionTypes.REQUEST_USER_LATEST_STAMPS,
      callAPI: StampAPI.requestUserLatestPage
    });
  },

  requestUserLikedPage(userId, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      userId: userId,
      idAttribute: 'userId',
      isInitialRequest: isInitialRequest,
      store: UserLikedStampStore,
      action: ActionTypes.REQUEST_USER_LIKED_STAMPS,
      callAPI: StampAPI.requestUserLikedPage
    });
  },

  requestZineLatestPage(zineSlug, isInitialRequest, allowInvalidate) {
    if (allowInvalidate && isInitialRequest) {
      // Keep stamps inside zines fresh
      AppDispatcher.handleViewAction({
        type: ActionTypes.INVALIDATE_ZINE_LATEST_STAMPS,
        zineSlug: zineSlug
      });
    }

    ActionUtils.requestIndexedPage({
      zineSlug: zineSlug,
      idAttribute: 'zineSlug',
      isInitialRequest: isInitialRequest,
      store: ZineLatestStampStore,
      action: ActionTypes.REQUEST_ZINE_LATEST_STAMPS,
      callAPI: StampAPI.requestZineLatestPage
    });
  },

  requestZinePendingPage(zineSlug, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      zineSlug: zineSlug,
      idAttribute: 'zineSlug',
      isInitialRequest: isInitialRequest,
      store: ZinePendingStampStore,
      action: ActionTypes.REQUEST_ZINE_PENDING_STAMPS,
      callAPI: StampAPI.requestZinePendingPage
    });
  },

  requestStampLikePage(stampSegment, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      stampSegment: stampSegment,
      idAttribute: 'stampSegment',
      getIdForStore: parseStampId,
      isInitialRequest: isInitialRequest,
      store: StampLikesStore,
      action: ActionTypes.REQUEST_STAMP_LIKES,
      callAPI: StampAPI.requestStampLikePage
    });
  },

  requestSearchPage(terms, isInitialRequest) {
    ActionUtils.requestIndexedPage({
      terms: terms,
      idAttribute: 'terms',
      isInitialRequest: isInitialRequest,
      store: SearchStampStore,
      action: ActionTypes.REQUEST_STAMP_SEARCH,
      callAPI: StampAPI.requestSearchPage
    });
  }
};

module.exports = StampActionCreators;



/** WEBPACK FOOTER **
 ** ./actions/StampActionCreators.js
 **/